import type { Address } from '@amarantos/api-client'
import {
	formatLength as formatLengthInLocale,
	formatOrdinate as formatOrdinateInLocale,
	formatSlopeValue as formatSlopeValueInLocale,
	LENGTH_FRACTION_DIGITS,
} from '@amarantos/shared'
import type { Locale } from 'date-fns'
import { format, isValid, parseISO } from 'date-fns'
import { enUS, pl } from 'date-fns/locale'
import i18n from 'i18next'

const DATE_FALLBACK = '-'

export function resolveDateLocale(locale?: string): Locale {
	switch (locale?.split('-')[0]?.toLowerCase()) {
		case 'pl':
			return pl
		default:
			return enUS
	}
}

export function formatAddress(address: Address): string {
	const streetLine = [address.street, address.number].filter(Boolean).join(' ')
	const cityLine = [address.postcode, address.city].filter(Boolean).join(' ')

	return [streetLine, cityLine].filter(Boolean).join(', ')
}

function formatISOValue(value: string | null | undefined, pattern: string, locale?: string): string {
	if (!value) return DATE_FALLBACK

	const parsed = parseISO(value)

	if (!isValid(parsed)) return DATE_FALLBACK

	return format(parsed, pattern, {
		locale: resolveDateLocale(locale ?? i18n.resolvedLanguage ?? i18n.language),
	})
}

export function formatISODate(value?: string | null, locale?: string): string {
	return formatISOValue(value, 'P', locale)
}

export function formatISODateTime(value?: string | null, locale?: string): string {
	return formatISOValue(value, 'Pp', locale)
}

/** Compact day.month + 24h time (e.g. "11.06 13:00") — for dense feeds where the full date reads as noise. */
export function formatISOShortDateTime(value?: string | null, locale?: string): string {
	return formatISOValue(value, 'dd.MM HH:mm', locale)
}

/** Placeholder rendered for missing/blank values — the one home for the em dash across the panel. */
export const EMPTY_VALUE = '—'

export function formatOrdinate(metres: number | null, locale?: string): string {
	if (metres == null) return EMPTY_VALUE
	return formatOrdinateInLocale(metres, locale ?? i18n.resolvedLanguage ?? i18n.language)
}

export function formatSlopeValue(perMille: number, locale?: string): string {
	return formatSlopeValueInLocale(perMille, locale ?? i18n.resolvedLanguage ?? i18n.language)
}

export function formatLength(metres: number, locale?: string): string {
	return formatLengthInLocale(metres, locale ?? i18n.resolvedLanguage ?? i18n.language)
}

/** A connection or segment length read against its pieces, so the centimetre the split works in stays visible. */
export function formatPreciseLength(metres: number, locale?: string): string {
	return formatLengthInLocale(metres, locale ?? i18n.resolvedLanguage ?? i18n.language, 2)
}

/**
 * A length summed over a whole network or project — same decimetre precision as `formatLength`
 * (ADR-0018), with the thousands grouping a single element's length never needs.
 */
export function formatTotalLength(metres: number, locale?: string): string {
	return new Intl.NumberFormat(locale ?? i18n.resolvedLanguage ?? i18n.language, {
		minimumFractionDigits: LENGTH_FRACTION_DIGITS,
		maximumFractionDigits: LENGTH_FRACTION_DIGITS,
	}).format(metres)
}
